import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useAtomValue, useSetAtom } from 'jotai';
import { Box, Button, FormControl, MenuItem, Select, Stack, SvgIcon } from '@mui/material';
import { BorderColor, ViewInAr as CubeIcon, ArrowDropDown } from '@mui/icons-material';
import { useRouter, useHavePermission } from '@/hooks';
import { ConditionType, Permissions, RemediatedStatus } from '@/types';
import { DataTable } from '@/components/shared/Table';
import { NameAndArea, UnitsEnum, UnitSystemEnum } from '@/__generated__/graphql';
import * as state from '@/components/Analysis/state';
import { localiseMeasurement } from '@/utils/unitSystem';
import { upperSnakeCase, parseNumberOrInfinity, convertTimeStampToDate } from '@/utils';
import { XlsxProcessor } from '@/utils/xlsx';
import { allPointOfInterestTemplates as allPointOfInterestTemplatesState } from '@/components/Analysis/state';
import {
  AllPaintRegionsForPaintBlockTableDocument,
  PaintRegionStatus,
  useUpdatePaintRegionStatusMutation,
  useAllPaintRegionsForPaintBlockTableQuery,
  PointOfInterestFindingAndName,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { primary } from '@/theme/colors';
import { PoiDialogBox } from './PoiDialogBox';
import { CreatePaintBlockReportModal } from './CreatePaintBlockReportModal';
import { FileCopyIcon } from '@/assets/icons/FileCopyIcon';
import { datePickerSlotProps } from '@/components/shared/ReportsRevamped/styles';
import { DatePicker } from '@mui/x-date-pickers';

type PointOfInterestTableProps = {
  template: Array<{ id: string; name: string }>;
  displayColumns: Array<string>;
  setPaintBlockTableXlsxProcessor: Dispatch<SetStateAction<XlsxProcessor | undefined>>;
  selectedConditionType: string;
  defects: Array<string> | undefined;
  defectsAreaByPaintBlocks: NameAndArea[];
  findingsByPaintBlock: PointOfInterestFindingAndName[] | undefined;
};

type RowData = {
  [index: string]: string | number;
};

export const PaintBlockTable = ({
  defectsAreaByPaintBlocks = [],
  template = [],
  displayColumns = [],
  setPaintBlockTableXlsxProcessor,
  selectedConditionType,
  defects,
  findingsByPaintBlock,
}: PointOfInterestTableProps) => {
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>({});
  const [isReportsModalOpen, setIsReportsModalOpen] = useState(false);
  const unitSystem = useAtomValue(state.unitSystem);
  const setSelectedAssemblyId = useSetAtom(state.selectedAssemblyId);
  const canAccess3DView = useHavePermission(Permissions.MANAGE_BETA_FEATURES);
  const allPointOfInterestTemplates = useAtomValue(allPointOfInterestTemplatesState);
  const [isRemediatedSettingsOpen, setIsRemediatedSettingsOpen] = useState(false);
  const [selectedPaintRegionId, setSelectedPaintRegionId] = useState('');
  const router = useRouter();
  const structureId = useGetStructureId();
  const [showTotalArea, setShowTotalArea] = useState(false);
  const [updatePaintRegionStatus] = useUpdatePaintRegionStatusMutation();
  const [isPoiDialogOpen, setIsPoiDialog] = useState<boolean>(false);
  const selectedPaintRegionGroup = useAtomValue(state.selectedPaintRegionGroup);

  const { data: paintRegionsData } = useAllPaintRegionsForPaintBlockTableQuery({
    variables: {
      structureId,
    },
    fetchPolicy: 'cache-first',
  });

  const toggleRemediatedSettingsOpen = useCallback((status: boolean) => {
    setIsRemediatedSettingsOpen(status);
    setIsPoiDialog(false);
  }, []);

  useEffect(() => {
    setShowTotalArea(selectedConditionType === ConditionType.COATING);
  }, [selectedConditionType]);

  useEffect(() => {
    const updatedValue: Record<string, boolean> = {};
    template?.forEach((field) => {
      updatedValue[field.name.toLowerCase().replaceAll(' ', '_')] = displayColumns.includes(
        field.name.toLowerCase()
      );
    });

    // Only update if there are changes
    // this is a workaround to prevent unnecessary re-renders
    // TODO: find a better solution because this is clearly not good design
    if (JSON.stringify(updatedValue) !== JSON.stringify(visibleColumns)) {
      setVisibleColumns(updatedValue);
    }
  }, [displayColumns, template, visibleColumns]);

  const render3dViewCell = useCallback(
    (parameters: GridRenderCellParams) => {
      const assemblyId = parameters.formattedValue;
      const handle3dView = (event: { stopPropagation: () => void }) => {
        event.stopPropagation();
        setSelectedAssemblyId(assemblyId);
        router.push(`/analysis/${router.query.inspection}/viewer`);
      };

      return assemblyId ? (
        <Box>
          <Button
            data-testid={`3d-view-${assemblyId}`}
            sx={{ height: 1 }}
            startIcon={<CubeIcon sx={{ color: '#35C2A3' }} />}
            onClick={handle3dView}
          >
            3D
          </Button>
        </Box>
      ) : (
        <></>
      );
    },
    [router, setSelectedAssemblyId]
  );

  const updateStatus = useCallback(
    async (paintRegionId: string, status: string) => {
      await updatePaintRegionStatus({
        variables: {
          input: {
            paintRegionId: paintRegionId,
            status: upperSnakeCase(status) as PaintRegionStatus,
          },
        },
        refetchQueries: [AllPaintRegionsForPaintBlockTableDocument],
      });
    },
    [updatePaintRegionStatus]
  );

  const renderStatusCell = useCallback(
    (parameters: GridRenderCellParams) => {
      const statuses = [
        'In Progress',
        'Scheduled',
        'Inspection pending',
        'No work required',
        'Remediated',
      ];
      const selection =
        paintRegionsData?.allPaintRegions?.filter((item) => item.id === parameters.row.id)[0]
          .status ?? PaintRegionStatus.InspectionPending;

      const handleChangeStatus = async (value: RemediatedStatus) => {
        setSelectedPaintRegionId(parameters?.row.id);
        if (value === RemediatedStatus.REMEDIATED) {
          // Update the status with today's date as remediation date
          const today = new Date().toISOString();
          await updatePaintRegionStatus({
            variables: {
              input: {
                paintRegionId: parameters?.row.id,
                status: upperSnakeCase(value) as PaintRegionStatus,
                remediationDate: today,
              },
            },
            refetchQueries: [AllPaintRegionsForPaintBlockTableDocument],
          });
          toggleRemediatedSettingsOpen(true);
        } else {
          updateStatus(parameters?.row.id, value);
        }
      };

      return (
        <Stack direction="row" alignItems="center" width="fit-content" sx={{ gap: 1 }}>
          <FormControl>
            <Select
              inputProps={{ sx: { pt: '10px', pb: '7px' } }}
              defaultValue={RemediatedStatus[selection]}
              renderValue={() => {
                return (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {RemediatedStatus[selection] === RemediatedStatus.REMEDIATED && (
                      <SvgIcon>
                        <CheckCircleIcon color="success" />
                      </SvgIcon>
                    )}
                    {RemediatedStatus[selection]}
                  </Box>
                );
              }}
              onChange={(event) => handleChangeStatus(event.target.value as RemediatedStatus)}
              data-testid="select-continent-dropdown"
            >
              {statuses?.map((status) => (
                <MenuItem
                  key={status}
                  value={status}
                  onClick={() => handleChangeStatus(status as RemediatedStatus)}
                >
                  {status}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {RemediatedStatus[selection] === RemediatedStatus.REMEDIATED && (
            <Button
              onClick={() => {
                setSelectedPaintRegionId(parameters?.row.id);
                setIsPoiDialog(true);
              }}
              sx={{ minWidth: '24px', background: 'transparent' }}
            >
              <BorderColor
                sx={{
                  border: '1px solid',
                  borderColor: primary,
                  borderRadius: 1,
                  p: '4px',
                }}
              />
            </Button>
          )}
        </Stack>
      );
    },
    [paintRegionsData?.allPaintRegions, toggleRemediatedSettingsOpen, updateStatus]
  );

  const renderRemediationDateCell = useCallback(
    (parameters: GridRenderCellParams) => {
      const paintRegion = paintRegionsData?.allPaintRegions?.find(
        (item) => item.id === parameters.row.id
      );
      const isRemediated = paintRegion?.status === PaintRegionStatus.Remediated;
      const remediationDate = paintRegion?.remediationDate;

      const handleDateChange = async (newDate: Date | null) => {
        if (newDate && paintRegion?.id) {
          await updatePaintRegionStatus({
            variables: {
              input: {
                paintRegionId: paintRegion.id,
                remediationDate: newDate.toISOString(),
              },
            },
            refetchQueries: [AllPaintRegionsForPaintBlockTableDocument],
          });
        }
      };

      const handleCellClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
      };

      const handleCellMouseDown = (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
      };

      return (
        <Stack
          direction="row"
          alignItems="center"
          width="fit-content"
          sx={{ gap: 1 }}
          data-testid="remediation-date-cell"
          onClick={handleCellClick}
          onMouseDown={handleCellMouseDown}
        >
          <DatePicker
            value={convertTimeStampToDate(remediationDate) || null}
            onChange={handleDateChange}
            format="dd MMM yyyy"
            disabled={!isRemediated}
            slotProps={{
              ...datePickerSlotProps,
              textField: {
                ...datePickerSlotProps.textField,
                variant: 'outlined',
                placeholder: 'N/A',
                onClick: handleCellClick,
                onMouseDown: handleCellMouseDown,
              },
            }}
            slots={{
              openPickerIcon: ArrowDropDown,
            }}
            data-testid="remediation-date-picker"
          />
        </Stack>
      );
    },
    [paintRegionsData?.allPaintRegions, updatePaintRegionStatus]
  );

  const tableSorting = useCallback(
    (a: string | number | null | undefined, b: string | number | null | undefined) => {
      // Handle null/undefined/empty string cases
      if (
        (a === null || a === undefined || a === '') &&
        (b === null || b === undefined || b === '')
      )
        return 0;
      if (a === null || a === undefined || a === '') return 1;
      if (b === null || b === undefined || b === '') return -1;

      // Convert to numbers if they're strings
      const numA = typeof a === 'number' ? a : parseNumberOrInfinity(a);
      const numB = typeof b === 'number' ? b : parseNumberOrInfinity(b);

      // Handle NaN cases after conversion
      if (isNaN(numA) && isNaN(numB)) return 0;
      if (isNaN(numA)) return 1;
      if (isNaN(numB)) return -1;

      return numA - numB;
    },
    []
  );

  // Define renderAreaCell to convert values based on current unit system
  const renderAreaCell = useCallback(
    (params: GridRenderCellParams) => {
      // For display in the UI, convert based on unit system
      if (typeof params.value === 'number') {
        // Convert to appropriate unit system
        const displayValue =
          unitSystem === UnitSystemEnum.Imperial
            ? params.value * 10.7639 // Convert m² to ft²
            : params.value; // Keep as m²

        // Format the number with 2 decimal places
        return displayValue.toFixed(2);
      }
      return params.value || '0';
    },
    [unitSystem]
  );

  // Memoize columns to prevent unnecessary re-renders
  const columns = useMemo(() => {
    const fieldColumns: GridColDef[] = [
      {
        field: 'block',
        headerName: 'BLOCK#',
        type: 'string',
        flex: 1,
        sortComparator: tableSorting,
      },
      {
        field: 'findings',
        headerName: 'TOTAL FINDINGS',
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        flex: 1,
        renderCell: (params) => <div data-testid={`${params.row.block}-count`}>{params.value}</div>,
      },
    ];
    if (showTotalArea) {
      fieldColumns.push({
        field: 'corrodedArea',
        headerName: `Total Corroded Area ${
          unitSystem === UnitSystemEnum.Imperial ? '(ft²)' : '(m²)'
        }`,
        type: 'number',
        flex: 1,
        renderCell: renderAreaCell,
        sortComparator: tableSorting,
      });
    }
    template?.forEach((field) => {
      const hasUnits = !Object.values(allPointOfInterestTemplates)
        .map((poiTemplate) => poiTemplate?.name.toUpperCase())
        .includes(field?.name?.toUpperCase());
      const units = unitSystem === UnitSystemEnum.Imperial ? '(ft²)' : '(m²)';
      const fieldName = field?.name.toLowerCase().replaceAll(' ', '_');

      fieldColumns.push({
        field: fieldName,
        headerName: `${field.name.toUpperCase()} ${hasUnits ? units : ''}`,
        type: 'number',
        flex: 1,
        renderCell: hasUnits ? renderAreaCell : undefined,
        sortComparator: tableSorting,
      });
    });
    if (canAccess3DView) {
      fieldColumns.push({
        headerName: 'VIEWS',
        field: 'assemblyId',
        type: 'string',
        flex: 1,
        renderCell: render3dViewCell,
      });
    }
    fieldColumns.push(
      {
        headerName: 'Status',
        field: 'status',
        type: 'custom',
        flex: 2.5,
        renderCell: renderStatusCell,
      },
      {
        headerName: 'REMEDIATION DATE',
        field: 'remediationDate',
        type: 'custom',
        flex: 2,
        renderCell: renderRemediationDateCell,
      },
      {
        headerName: 'Create Report',
        field: '',
        flex: 2,
        type: 'custom',
        renderCell: (value) => (
          <Button
            onClick={() => {
              const paintBlockId = value?.id.toString();
              setSelectedPaintRegionId(paintBlockId);
              setIsReportsModalOpen(true);
            }}
            variant="outlined"
            sx={{ height: '70%', fontSize: '1.4rem', width: 'fit-content' }}
            startIcon={<FileCopyIcon />}
          >
            Create Report
          </Button>
        ),
      }
    );

    return fieldColumns;
  }, [
    allPointOfInterestTemplates,
    canAccess3DView,
    render3dViewCell,
    renderStatusCell,
    renderRemediationDateCell,
    showTotalArea,
    tableSorting,
    template,
    unitSystem,
    renderAreaCell,
  ]);

  const rows: RowData[] = useMemo(() => {
    return (
      paintRegionsData?.allPaintRegions
        ?.filter((paintRegion) => {
          // if no group is selected, include all regions
          if (!selectedPaintRegionGroup?.id) return true;
          return paintRegion?.paintRegionGroup?.id === selectedPaintRegionGroup.id;
        })
        .map((paintRegion) => {
          const corrodedArea = defectsAreaByPaintBlocks?.find(
            (area) => area.name === paintRegion.name
          );

          // Calculate total area as raw number for export
          const totalArea =
            corrodedArea?.totalArea?.reduce((accumulator, currentValue) => {
              return (
                accumulator +
                (defects?.includes(currentValue?.type.toLowerCase().replaceAll(' ', '_') ?? '')
                  ? currentValue?.value ?? 0
                  : 0)
              );
            }, 0) ?? 0;

          // Store raw numbers for export
          const corrodedAreaTypes: { [key: string]: number } = {};
          corrodedArea?.totalArea?.forEach((areaType) => {
            if (areaType?.type && areaType?.value) {
              const type = areaType.type.toLowerCase().replaceAll(' ', '_');
              corrodedAreaTypes[type] =
                Number(
                  localiseMeasurement({
                    unitSystem,
                    sourceUnit: UnitsEnum.SquareMeter,
                    value: areaType.value,
                    roundToDecimal: 2,
                    showUnitLabel: false,
                  })
                ) ?? 0;
              corrodedAreaTypes[type] = areaType.value;
            }
          });

          const poiFindingByPaintBlock = findingsByPaintBlock?.find(
            ({ name }) => name === paintRegion.name
          );

          const rowData: RowData = {
            id: paintRegion.id,
            block: paintRegion.name,
            corrodedArea: totalArea ?? 0,
            findings: poiFindingByPaintBlock?.totalCount ?? 0,
            ...corrodedAreaTypes,
          };

          poiFindingByPaintBlock?.pointOfInterestFindings?.forEach((fieldData) => {
            if (fieldData?.type && fieldData?.count) {
              rowData[fieldData.type.toLowerCase()] = fieldData.count;
            }
          });

          return rowData;
        }) ?? []
    );
  }, [
    paintRegionsData?.allPaintRegions,
    selectedPaintRegionGroup?.id,
    defectsAreaByPaintBlocks,
    unitSystem,
    defects,
    findingsByPaintBlock,
    findingsByPaintBlock,
    defects,
  ]);

  const cols = useMemo(() => {
    // Get visible columns in the correct order
    const visibleColumns = columns.filter((column) => !column.hideable);

    // Process each row to ensure data is in the correct column order and format
    const processedData = rows.map((row) => {
      return visibleColumns.map((col) => {
        const value = row[col.field];
        // Check if this is a column that should have units (uses renderAreaCell)
        const hasUnits = col.renderCell === renderAreaCell;

        if (hasUnits && typeof value === 'number') {
          // For export, we want the raw number in the current unit system
          // The value is stored in m², so we need to convert if using imperial
          if (unitSystem === UnitSystemEnum.Imperial) {
            return value * 10.7639; // Convert m² to ft²
          }
          return value;
        }
        return value ?? '';
      });
    });

    // Transpose the data for XLSX export (each column becomes an array of values)
    const exportedData = visibleColumns.map((col, colIndex) => ({
      label: col.headerName ?? 'no label',
      data: processedData.map((row) => row[colIndex]),
    }));

    return exportedData;
  }, [columns, rows, unitSystem, renderAreaCell]);

  const xlsx = useMemo(
    () =>
      new XlsxProcessor({
        columns: cols,
        sheetName: 'Paint Block Data',
      }),
    [cols]
  );

  useEffect(() => {
    setPaintBlockTableXlsxProcessor(xlsx);
  }, [xlsx, setPaintBlockTableXlsxProcessor]);

  return (
    <>
      <DataTable
        columns={columns}
        rows={rows}
        pageSizeOptions={[25, 50, 100]}
        selected={false}
        visibleColumns={visibleColumns}
        setVisiblecolumns={setVisibleColumns}
      />
      <PoiDialogBox
        isOpen={isPoiDialogOpen || isRemediatedSettingsOpen}
        toggleDialog={toggleRemediatedSettingsOpen}
        paintRegionId={selectedPaintRegionId}
      />
      <CreatePaintBlockReportModal
        onClose={() => setIsReportsModalOpen(false)}
        open={isReportsModalOpen}
        paintRegionId={selectedPaintRegionId}
      />
    </>
  );
};
